<template>
  <div class="home">
    <HomeBanner />
    <HomeWhatIs />
    <HomeHowItWorks />
    <HomeShowcase @start="triggerStart('showcase')" />
    <HomeUseCases @start="triggerStart('use_cases')" />
    <HomeWords @start="triggerStart('words')" />
    <HomeFAQ />

    <!-- Final CTA Section -->
    <section class="final-cta">
      <h2>{{ $t('home.finalCtaTitle') }}</h2>
      <button class="btn-final-cta" @click="triggerStart('final_cta')">{{ $t('home.finalCtaBtn') }} <span class="cta-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></span></button>
      <p class="final-cta__hint">{{ $t('home.finalCtaHint') }}</p>
    </section>

    <!-- Floating Coupon Badge -->
    <div class="coupon-float" :class="{ 'coupon-float--collapsed': couponCollapsed }" @click="toggleCoupon">
      <div class="coupon-float__tag" v-if="couponCollapsed">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
          <path d="M21 5H3a2 2 0 00-2 2v4a2 2 0 002 2h0a2 2 0 010 4h0a2 2 0 00-2 2v2a2 2 0 002 2h18a2 2 0 002-2v-2a2 2 0 00-2-2h0a2 2 0 010-4h0a2 2 0 002-2V7a2 2 0 00-2-2z"/>
        </svg>
        <span>20% OFF</span>
      </div>
      <div class="coupon-float__card" v-else>
        <div class="coupon-float__close" @click.stop="couponCollapsed = true">&times;</div>
        <div class="coupon-float__discount">{{ $t('home.couponTitle') }}</div>
        <div class="coupon-float__countdown">
          <span class="countdown-item">{{ countdown.hours }}<small>h</small></span>
          <span class="countdown-sep">:</span>
          <span class="countdown-item">{{ countdown.minutes }}<small>m</small></span>
          <span class="countdown-sep">:</span>
          <span class="countdown-item">{{ countdown.seconds }}<small>s</small></span>
        </div>
        <div class="coupon-float__code" @click.stop="copyCoupon">
          <span class="code-text">SAVE20</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
          </svg>
        </div>
        <div class="coupon-float__hint">{{ copied ? $t('home.couponCopied') : $t('home.couponHint') }}</div>
      </div>
    </div>

    <!-- Social Network Modal -->
    <div class="connect-modal-mask" v-if="showSocialModal" @click.self="showSocialModal = false">
      <div class="connect-modal">
        <div class="connect-modal__close" @click="showSocialModal = false">&times;</div>
        <h3 class="connect-modal__title">{{ $t('home.connectModalTitle') }}</h3>
        <div class="connect-modal__options">
          <a class="connect-option" :href="facebookLoginPath">
            <svg viewBox="0 0 24 24" fill="#1877F2" width="40" height="40">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span>Facebook</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/layout/Home.scss'
import HomeBanner from './Home/Banner'
import HomeHowItWorks from './Home/HowItWorks'
import HomeShowcase from './Home/Showcase'
import HomeUseCases from './Home/UseCases'
import HomeWords from './Home/Words'
import HomeFAQ from './Home/FAQ'
import HomeWhatIs from './Home/WhatIs'
import Cookies from 'js-cookie'
import { mapGetters } from 'vuex'
import config from 'src/app.config'
import { track } from 'src/utils/track'
import { buildMeta } from 'src/utils/seo'

export default {
  name: 'home',
  metaInfo () {
    return buildMeta({
      // 不传 title 走 default：iPastBook - Turn Your Social Media Into a Beautiful Book
      path: '/'
    })
  },
  components: {
    HomeBanner,
    HomeHowItWorks,
    HomeShowcase,
    HomeUseCases,
    HomeWords,
    HomeFAQ,
    HomeWhatIs
  },
  data () {
    return {
      couponCollapsed: window.innerWidth <= 768,
      copied: false,
      showSocialModal: false,
      countdown: { hours: '23', minutes: '59', seconds: '59' },
      countdownTimer: null
    }
  },
  computed: {
    ...mapGetters([
      'language', 'user'
    ]),
    facebookLoginPath () {
      return config.facebookLoginPath
    },
    title () {
      return this.$t('title.home')
    }
  },
  created () {
    this.checkSource()
    this.startCountdown()
    this.$root.$on('show-social-modal', () => {
      this.triggerStart('global_event')
    })
  },
  beforeDestroy () {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    this.$root.$off('show-social-modal')
  },
  methods: {
    // GA4 漏斗：用户点首页 CTA 进入「开始制作」流程
    triggerStart (source) {
      this.showSocialModal = true
      try {
        track('select_content', {
          content_type: 'home_cta',
          item_id: source || 'unknown'
        })
      } catch (e) {}
    },
    startCountdown () {
      const storageKey = 'coupon_end_time'
      let endTime = localStorage.getItem(storageKey)
      if (!endTime || Number(endTime) < Date.now()) {
        endTime = Date.now() + 24 * 60 * 60 * 1000
        localStorage.setItem(storageKey, endTime)
      }
      const update = () => {
        const diff = Math.max(0, Number(endTime) - Date.now())
        const h = Math.floor(diff / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        const s = Math.floor((diff % 60000) / 1000)
        this.countdown = {
          hours: String(h).padStart(2, '0'),
          minutes: String(m).padStart(2, '0'),
          seconds: String(s).padStart(2, '0')
        }
        if (diff === 0) {
          localStorage.removeItem(storageKey)
          this.startCountdown()
        }
      }
      update()
      this.countdownTimer = setInterval(update, 1000)
    },
    toggleCoupon () {
      if (this.couponCollapsed) {
        this.couponCollapsed = false
      }
    },
    copyCoupon () {
      const text = 'SAVE20'
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.copied = true
          setTimeout(() => { this.copied = false }, 2000)
        })
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      }
    },
    checkSource () {
      let { sourceId, ref } = this.$route.query
      sourceId = sourceId || ref
      if (sourceId && !this.user.uid) {
        Cookies.set('sourceId', sourceId, { expires: 90 })
      }
    }
  }
}
</script>

<style lang="scss">
.home {
  & > section {
    padding: 4rem 1rem;
    position: relative;
    header {
      text-align: center;
      margin-bottom: 3rem;
      h2 {
        font-size: 2.25rem;
        color: #1a1a2e;
        font-weight: 700;
        margin-bottom: 0.75rem;
      }
      .subtitle {
        font-size: 1.125rem;
        color: #6b7280;
        max-width: 600px;
        margin: 0 auto;
        line-height: 1.6;
      }
    }
    & > .container {
      padding: 0 2rem;
    }
  }
}
/* Floating Coupon Badge */
.coupon-float {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 100;
  cursor: pointer;
  animation: couponSlideIn 0.5s ease-out;
}

@keyframes couponSlideIn {
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.coupon-float__tag {
  display: flex;
  align-items: center;
  gap: 6px;
  background: $el-color-primary;
  color: #fff;
  padding: 10px 16px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 16px rgba(81, 45, 202, 0.35);
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.05);
  }
}

.coupon-float__card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  position: relative;
  border-top: 4px solid $el-color-primary;
}

.coupon-float__close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #374151; }
}

.coupon-float__discount {
  font-size: 1.5rem;
  font-weight: 800;
  color: $el-color-primary;
  margin-bottom: 10px;
}

.coupon-float__countdown {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 12px;
  .countdown-item {
    background: #1a1a2e;
    color: #fff;
    font-size: 1.125rem;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 6px;
    min-width: 40px;
    text-align: center;
    small {
      font-size: 0.625rem;
      font-weight: 400;
      margin-left: 1px;
    }
  }
  .countdown-sep {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1a1a2e;
  }
}

.coupon-float__code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f3f0ff;
  border: 2px dashed $el-color-primary;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #ebe5ff; }
  .code-text {
    font-size: 1.125rem;
    font-weight: 700;
    color: $el-color-primary;
    letter-spacing: 2px;
  }
  svg { color: $el-color-primary; }
}

.coupon-float__hint {
  margin-top: 8px;
  font-size: 0.8125rem;
  color: #6b7280;
  text-align: center;
}

/* Final CTA Section */
.final-cta {
  background: $el-color-primary;
  padding: 5rem 2rem;
  text-align: center;

  h2 {
    color: #fff;
    font-size: 2rem;
    font-weight: 700;
    max-width: 900px;
    margin: 0 auto 2rem;
    line-height: 1.5;
    white-space: nowrap;
  }

  .btn-final-cta {
    display: inline-block;
    padding: 1rem 3.5rem;
    background: #fff;
    color: $el-color-primary;
    font-size: 1.125rem;
    font-weight: 700;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    transition: all 0.25s;

    &:hover {
      background: linear-gradient(to right, #F56368, #ff878b);
      color: #fff;
      transform: translateY(-2px);
      box-shadow: 0 6px 24px rgba(124, 58, 237, 0.4);
    }
  }

  &__hint {
    margin-top: 1rem;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.875rem;
  }
}

@media (max-width: 768px) {
  .final-cta {
    padding: 3.5rem 1.5rem;

    h2 {
      font-size: 1.25rem;
      white-space: normal;
    }

    .btn-final-cta {
      padding: 0.875rem 2.5rem;
      font-size: 1rem;
    }
  }

  .home {
    & > section {
      padding: 3rem 1rem;
      header {
        margin-bottom: 2rem;
        h2 {
          font-size: 1.5rem;
        }
        .subtitle {
          font-size: 1rem;
        }
      }
      & > .container {
        padding: 0 1rem !important;
      }
    }
  }
  .coupon-float {
    bottom: 16px;
    left: 16px;
  }
  .coupon-float__card {
    min-width: 190px;
    padding: 16px 18px;
  }
}
</style>
